import { useEffect, useRef } from 'react';
import { buildAcFormThemeCss } from './acFormThemeCss';

/**
 * ActiveCampaignEmbed component
 * Allows you to paste Active Campaign form code directly and it will execute properly
 *
 * This component uses a ref-based approach to prevent double initialization:
 * - Tracks which HTML content has been initialized using a ref
 * - Skips re-initialization if the same content is already loaded
 * - Works correctly with React Strict Mode (which causes double mounting)
 * - Allows different forms to load but prevents duplicate initialization
 *
 * Styling: the AC export is injected verbatim, then an override stylesheet
 * generated by buildAcFormThemeCss() is appended after it, re-theming the form
 * to the site's design tokens (dark surfaces, Manrope, brand-green pill CTA).
 *
 * @param {Object} props - Component props
 * @param {string} props.htmlContent - The raw HTML content from Active Campaign (including scripts)
 * @param {Record<string, string>} [props.prefill] - Field values to pre-populate, keyed by the form input's `name` (e.g. `{ email: 'a@b.com' }`)
 * @param {boolean} [props.hideTitle] - Hide the export's built-in `._form-title`; set it when the page already renders its own heading above the embed
 * @returns {import('react').ReactElement} A div container for the Active Campaign form
 */
function ActiveCampaignEmbed({ htmlContent, prefill, hideTitle = false }) {
  const containerRef = useRef(null);
  /** @type {{current: string | null}} */
  const initializedContentRef = useRef(null);

  useEffect(() => {
    if (!htmlContent || !containerRef.current) {
      return;
    }

    /** @type {HTMLDivElement} */
    const container = containerRef.current;

    // If we've already initialized THIS content, skip everything
    if (initializedContentRef.current === htmlContent) {
      console.log(
        '[ActiveCampaign] Already initialized this content, skipping re-initialization'
      );
      return;
    }

    // Mark this content as initialized BEFORE doing any work
    initializedContentRef.current = htmlContent;
    console.log('[ActiveCampaign] Initializing form content');

    // Clear and reinitialize
    container.innerHTML = '';

    // Create a wrapper div to hold all content
    const wrapper = document.createElement('div');
    wrapper.innerHTML = htmlContent;

    // Extract and inject scripts properly
    const scripts = wrapper.querySelectorAll('script');

    // Append non-script content first
    while (wrapper.firstChild) {
      if (wrapper.firstChild.nodeName !== 'SCRIPT') {
        container.appendChild(wrapper.firstChild);
      } else {
        wrapper.removeChild(wrapper.firstChild);
      }
    }

    // Some exports ship a whitespace-only title (e.g. the subscribe form);
    // drop those so they don't leave a stray gap above the first field.
    container.querySelectorAll('._form-title').forEach((titleEl) => {
      if (!titleEl.textContent.trim()) {
        titleEl.remove();
      }
    });

    // Re-theme the injected form(s) to the site's design tokens. The override
    // stylesheet is appended AFTER the form's own <style> blocks so its
    // declarations win the cascade (see acFormThemeCss.js for the specificity
    // strategy). This keeps the AC exports verbatim while the rendered form
    // follows the shipped brand palette.
    const formIds = Array.from(container.querySelectorAll('form._form'))
      .map((form) => form.id)
      .filter(Boolean);
    if (formIds.length > 0) {
      const themeStyle = document.createElement('style');
      themeStyle.textContent = formIds.map(buildAcFormThemeCss).join('\n');
      container.appendChild(themeStyle);
    }

    // Then handle scripts one by one
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');

      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy content
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }

      // For external scripts (has src attribute), check if it already exists in document
      if (oldScript.src) {
        const existingScript = document.querySelector(
          `script[src="${oldScript.src}"]`
        );
        if (existingScript) {
          console.log(
            '[ActiveCampaign] Script already exists, skipping:',
            oldScript.src
          );
          return; // Skip - script already in DOM
        }
        // Add external scripts to document.body (so they persist)
        document.body.appendChild(newScript);
        console.log(
          '[ActiveCampaign] Added external script to document.body:',
          oldScript.src
        );
      } else {
        // Add inline scripts to container (they need to be in context)
        container.appendChild(newScript);
        console.log('[ActiveCampaign] Added inline script to container');
      }
    });
  }, [htmlContent]);

  // Pre-fill fields the visitor already gave us elsewhere (e.g. the email typed
  // into the footer subscribe card). Serialised so a fresh object literal from
  // the parent does not re-run the effect on every render.
  const prefillKey = JSON.stringify(prefill ?? {});

  useEffect(() => {
    /** @type {Record<string, string>} */
    const values = JSON.parse(prefillKey);
    const fieldNames = Object.keys(values).filter((name) => values[name]);

    if (!htmlContent || fieldNames.length === 0 || !containerRef.current) {
      return undefined;
    }

    /** @type {HTMLDivElement} */
    const container = containerRef.current;

    // The form markup is injected imperatively by the effect above and the
    // ActiveCampaign scripts finish wiring it up a beat later, so poll briefly
    // until every field we care about exists.
    const applyPrefill = () =>
      fieldNames.every((name) => {
        /** @type {HTMLInputElement | null} */
        const field = container.querySelector(`[name="${CSS.escape(name)}"]`);
        if (!field) {
          return false;
        }
        field.value = values[name];
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      });

    if (applyPrefill()) {
      return undefined;
    }

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (applyPrefill() || attempts >= 20) {
        clearInterval(timer);
      }
    }, 100);

    return () => clearInterval(timer);
  }, [htmlContent, prefillKey]);

  // The wrapper is a plain transparent div: the injected form sits directly on
  // the surrounding card/section surface, and buildAcFormThemeCss() re-themes
  // every AC element to the live `--color-*` tokens.
  return (
    <div
      ref={containerRef}
      className={hideTitle ? 'ac-embed--hide-title' : undefined}
    />
  );
}

export default ActiveCampaignEmbed;
